import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Spin } from 'antd';
import { catalogueRequest } from './api';

type Point = {
  timestamp: string;
  bid: number | null;
  ask: number | null;
  is_synthetic: boolean;
};
type History = { points: Point[]; truncated: boolean };

export default function PricePreview({
  assetId,
  admin,
  sessionId,
}: {
  assetId: number;
  admin: boolean;
  sessionId?: number;
}) {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [window, setWindow] = useState({ start: '', end: '', revision: 0 });
  const [data, setData] = useState<History>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setData(undefined);
    catalogueRequest<History>(
      `assets/${assetId}/history`,
      'GET',
      {
        scope: admin ? 'admin' : 'selection',
        ...(sessionId !== undefined ? { session_id: sessionId } : {}),
        ...(window.start ? { start: window.start } : {}),
        ...(window.end ? { end: window.end } : {}),
      },
      controller.signal,
    )
      .then(result => {
        if (!controller.signal.aborted) setData(result);
      })
      .catch(e => {
        if (!controller.signal.aborted) setError(e.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [assetId, admin, sessionId, window]);
  const chart = useMemo(() => {
    if (!data?.points.length) return null;
    const values = data.points
      .flatMap(p => [p.bid, p.ask])
      .filter((v): v is number => v !== null && Number.isFinite(v));
    if (!values.length) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || Math.abs(max) * 0.01 || 1;
    const startTime = Date.parse(data.points[0].timestamp);
    const timeSpan =
      Date.parse(data.points[data.points.length - 1].timestamp) - startTime ||
      1;
    const path = (field: 'bid' | 'ask') => {
      let continuing = false;
      return data.points
        .map(p => {
          const value = p[field];
          if (value === null || !Number.isFinite(value)) {
            continuing = false;
            return '';
          }
          const command = continuing ? 'L' : 'M';
          continuing = true;
          return `${command}${50 + ((Date.parse(p.timestamp) - startTime) / timeSpan) * 440},${170 - ((value - min) / span) * 150}`;
        })
        .join(' ');
    };
    return { min, max, bid: path('bid'), ask: path('ask') };
  }, [data]);
  return (
    <div className="ac-price-preview">
      <strong>Stored daily bid / ask</strong>
      <div className="ac-toolbar">
        <label>
          From
          <Input
            type="date"
            value={start}
            onChange={e => setStart(e.target.value)}
          />
        </label>
        <label>
          To
          <Input
            type="date"
            value={end}
            onChange={e => setEnd(e.target.value)}
          />
        </label>
        <Button
          onClick={() =>
            setWindow(current => ({
              start,
              end,
              revision: current.revision + 1,
            }))
          }
        >
          Load quotes
        </Button>
      </div>
      {loading && <Spin />}
      {error && <Alert type="error" title={error} />}
      {data &&
        (chart ? (
          <>
            <svg
              viewBox="0 0 510 200"
              role="img"
              aria-label="Stored daily bid and ask quotes"
              style={{ width: '100%' }}
            >
              <text x="0" y="20" fontSize="11">
                {chart.max.toPrecision(4)}
              </text>
              <text x="0" y="172" fontSize="11">
                {chart.min.toPrecision(4)}
              </text>
              <path
                d={chart.bid}
                fill="none"
                stroke="#2463ad"
                strokeWidth="1.7"
              />
              <path
                d={chart.ask}
                fill="none"
                stroke="#ad7824"
                strokeWidth="1.7"
              />
              <text x="50" y="194" fontSize="11">
                {data.points[0].timestamp.slice(0, 10)}
              </text>
              <text x="490" y="194" textAnchor="end" fontSize="11">
                {data.points[data.points.length - 1].timestamp.slice(0, 10)}
              </text>
            </svg>
            <small>
              Blue: bid · Ochre: ask · {data.points.length} stored bars
              {data.truncated ? ' (latest 500 in this window)' : ''}
              {data.points.some(p => p.is_synthetic)
                ? ' · Includes previously filled bars'
                : ''}
            </small>
          </>
        ) : (
          <p>No stored daily bid / ask quotes in this window.</p>
        ))}
      <p className="ac-muted">
        Reference data in the product’s stored quote units. This preview does
        not check session playability or calculate product valuations.
      </p>
    </div>
  );
}
